import { type SendFeedbackRequestError } from '@aikoa/api'
import {
  type DisplayContact,
  type FeedbackRowWithAsset,
  feedbackSendingMethodSchema,
  sendScheduleKeys,
  type SendScheduleKey,
  truncateName,
} from '@aikoa/common'
import { Trans, useTranslations, type TFunction } from '@aikoa/i18n/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { View } from 'react-native'
import { z } from 'zod/v4'
import { ContactConsentConfirmation } from '~/components/contacts/ContactConsentConfirmation'
import { ContactRow } from '~/components/contacts/ContactRow'
import { MultiRowContactSelect } from '~/components/contacts/MultiRowContactSelect'
import { useFeedbackErrorMessage } from '~/components/feedback/feedbackErrorMessages'
import { FeedbackRequestAcknowledgement } from '~/components/feedback/FeedbackRequestAcknowledgement'
import {
  FeedbackRequestCreationMethodPhase,
  FeedbackRequestInboundEmailPhase,
  FeedbackRequestSupportPhase,
  type FeedbackRequestCreationMethod,
} from '~/components/feedback/FeedbackRequestCreationMethodPhase'
import { FeedbackSendingSettingsModal } from '~/components/feedback/FeedbackSendingSettingsModal'
import { createFlowPhases, FlowProvider, type FlowData } from '~/components/flows/FlowProvider'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { type AlertAction, useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon'
import { InfoModal } from '~/components/InfoModal'
import { InterestFlowBottomSheet } from '~/components/interests/InterestFlowBottomSheet'
import { SectionContent, SectionTitle } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import { Body, Label } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import { Button } from '~/components/ui/button'
import { InfoAlert } from '~/components/ui/info-alert'
import { Text } from '~/components/ui/text'
import { useUserId } from '~/components/user/SessionProvider'
import { CONTACTS_QUERY_KEY, useContactsByIds, useHasAnyContactsForFeedback } from '~/data/contacts'
import { useUserInboundAddress } from '~/data/user/inboundAddress'
import { isUserProfileComplete, useUserProfile } from '~/data/user/userProfile'
import { trpc, trpcClient } from '~/lib/trpc'
import { cn } from '~/lib/utils'

type ContactFeedbackEligibility = {
  canSend: boolean
  reason: 'ok' | 'answered' | 'cooldown' | 'alreadyRequested' | 'contactDisabled'
  nextAllowedAt?: Date
}

const GENERAL_FEEDBACK_COOLDOWN_MONTHS = 4
const requestCreationMethodValueSchema = z.enum(['manual', 'inbound-email', 'contact-support'])
const requestCreationMethodPhaseDataSchema = z.object({
  creationMethod: requestCreationMethodValueSchema.optional(),
})
const requestCreationMethodSchema = requestCreationMethodPhaseDataSchema.refine(
  (phaseData) => phaseData.creationMethod !== undefined,
  {
    path: ['creationMethod'],
    message: 'Creation method is required',
  }
)
const inboundEmailPhaseDataSchema = z.object({
  hadInboundAddressBeforeEntering: z.boolean().default(false),
  hasSavedToContacts: z.boolean().default(false),
})
const selectContactPhaseDataSchema = z.object({
  contactIds: z.array(z.string()).max(10).default([]),
  contactConsentConfirmed: z.boolean().default(false),
})

const selectContactSchema = selectContactPhaseDataSchema.refine(
  (phaseData) => phaseData.contactConsentConfirmed,
  {
    path: ['contactConsentConfirmed'],
    message: 'Contact consent confirmation is required',
  }
)

type SelectContactPhaseData = z.infer<typeof selectContactPhaseDataSchema>
type RequestCreationMethodPhaseData = z.infer<typeof requestCreationMethodPhaseDataSchema>
type InboundEmailPhaseData = z.infer<typeof inboundEmailPhaseDataSchema>

const requestConfigSchema = z.object({
  sendTiming: sendScheduleKeys,
  sendingMethod: feedbackSendingMethodSchema,
})

const feedbackRequestFlowDataSchema = z.object({
  'select-creation-method': requestCreationMethodSchema,
  'select-contact': selectContactSchema,
  'request-config': requestConfigSchema,
})

export type FeedbackRequestFlowData = z.infer<typeof feedbackRequestFlowDataSchema>

function getInitialRequestCreationMethodPhaseData(): RequestCreationMethodPhaseData {
  return {
    creationMethod: undefined,
  }
}

function parseRequestCreationMethodPhaseData(data: unknown): RequestCreationMethodPhaseData {
  const parsedPhaseData = requestCreationMethodPhaseDataSchema.safeParse(data)

  if (parsedPhaseData.success) {
    return parsedPhaseData.data
  }

  return getInitialRequestCreationMethodPhaseData()
}

function getRequestCreationMethod(
  data: FlowData | undefined
): FeedbackRequestCreationMethod | undefined {
  return parseRequestCreationMethodPhaseData(data?.['select-creation-method']).creationMethod
}

function getInitialInboundEmailPhaseData(): InboundEmailPhaseData {
  return {
    hadInboundAddressBeforeEntering: false,
    hasSavedToContacts: false,
  }
}

function parseInboundEmailPhaseData(data: unknown): InboundEmailPhaseData {
  const parsedPhaseData = inboundEmailPhaseDataSchema.safeParse(data)

  if (parsedPhaseData.success) {
    return parsedPhaseData.data
  }

  return getInitialInboundEmailPhaseData()
}

function getInitialSelectContactPhaseData(initialContactId?: string): SelectContactPhaseData {
  return {
    contactIds: initialContactId ? [initialContactId] : [],
    contactConsentConfirmed: false,
  }
}

function parseSelectContactPhaseData(
  data: unknown,
  initialContactId?: string
): SelectContactPhaseData {
  const parsedPhaseData = selectContactPhaseDataSchema.safeParse(data)

  if (parsedPhaseData.success) {
    return parsedPhaseData.data
  }

  return getInitialSelectContactPhaseData(initialContactId)
}

function hasFeedbackAnswers(feedbackRow: FeedbackRowWithAsset): boolean {
  if (feedbackRow.completed_at) {
    return true
  }

  if (feedbackRow.declined_at) {
    return true
  }

  return false
}

function getContactFeedbackEligibility(contact: DisplayContact): ContactFeedbackEligibility {
  const contactIsDisabled =
    contact.disabled_at !== null &&
    contact.disabled_at !== undefined &&
    contact.disabled_reason !== 'RESERVED'

  if (contactIsDisabled) {
    return { canSend: false, reason: 'contactDisabled' }
  }

  const feedbackRows = (contact.feedback ?? []) as FeedbackRowWithAsset[]
  const hasPending = feedbackRows.some((row) => !row.completed_at && !hasFeedbackAnswers(row))

  if (hasPending) {
    return { canSend: false, reason: 'alreadyRequested' }
  }

  const latestCompleted = feedbackRows
    .filter((row) => !!row.completed_at)
    .sort(
      (rowA, rowB) =>
        new Date(rowB.completed_at!).getTime() - new Date(rowA.completed_at!).getTime()
    )[0]

  if (latestCompleted?.completed_at) {
    const nextAllowedAt = dayjs(latestCompleted.completed_at)
      .add(GENERAL_FEEDBACK_COOLDOWN_MONTHS, 'month')
      .toDate()

    if (new Date() < nextAllowedAt) {
      return { canSend: false, reason: 'cooldown', nextAllowedAt }
    }
  }

  return { canSend: true, reason: 'ok' }
}

function getFeedbackEligibilityErrorMessage(
  t: TFunction<'feedback'>,
  eligibility: ContactFeedbackEligibility,
  includeActionHint: boolean
): string {
  let baseMessage: string

  switch (eligibility.reason) {
    case 'answered':
      baseMessage = t(($) => $.errors.contactAlreadyAnswered)
      break
    case 'cooldown':
      if (eligibility.nextAllowedAt) {
        baseMessage = t(($) => $.errors.alreadyAnsweredByContactCooldown, {
          nextTime: dayjs(eligibility.nextAllowedAt).format('DD.MM.YYYY HH:mm'),
        })
      } else {
        baseMessage = t(($) => $.errors.cannotSendToCooldown)
      }
      break
    case 'contactDisabled':
      baseMessage = t(($) => $.errors.contactDisabled)
      break
    case 'alreadyRequested':
      baseMessage = t(($) => $.errors.requestAlreadySent)
      break
    default:
      baseMessage = t(($) => $.errors.cannotSendToCooldown)
      break
  }

  if (!includeActionHint) {
    return baseMessage
  }

  const hint = t(($) => $.errors.selectOtherContactOrAsset)

  return `${baseMessage} ${hint}`
}

const IncompleteProfileDialogContent = () => {
  const { t } = useTranslations('feedback')
  return (
    <View className="gap-4">
      <SectionTitle className="text-center">
        {t(($) => $.requestFlow.incompleteProfile)}
      </SectionTitle>
      <Body className="text-center text-lg/relaxed">
        {t(($) => $.requestFlow.completeProfileDescription)}
      </Body>
      <InfoModal
        triggerLabel={t(($) => $.requestFlow.whyImportant)}
        triggerVariant="outline"
        className="w-full max-w-md gap-8 pt-8">
        <View className="gap-5">
          <SectionTitle className="text-center">
            {t(($) => $.requestFlow.whyImportant)}
          </SectionTitle>
          <View className="gap-2 px-3">
            <View className="flex-row items-start gap-3">
              <View className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-500" />
              <Body className="flex-1 text-pretty text-lg/relaxed">
                <Text className="font-semibold">{t(($) => $.requestFlow.trustImmediately)}:</Text>{' '}
                {t(($) => $.requestFlow.trustImmediatelyDescription)}
              </Body>
            </View>
            <View className="flex-row items-start gap-3">
              <View className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-500" />
              <Body className="flex-1 text-pretty text-lg/relaxed">
                <Text className="font-semibold">{t(($) => $.requestFlow.moreConversions)}:</Text>{' '}
                {t(($) => $.requestFlow.moreConversionsDescription)}
              </Body>
            </View>
          </View>
        </View>
      </InfoModal>
    </View>
  )
}

function useIncompleteProfileDialog() {
  const { showAlert } = useAlert()
  const { t } = useTranslations('feedback')
  const router = useRouter()

  return useCallback(() => {
    showAlert({
      type: 'error',
      children: <IncompleteProfileDialogContent />,
      actions: [
        {
          label: t(($) => $.requestFlow.editProfile),
          buttonVariant: 'primary',
          buttonSize: 'lg',
          onPress: () => router.replace('/(authenticated)/profile'),
          shouldClose: true,
        },
      ],
      cancelButton: {
        label: t(($) => $.requestFlow.exit),
        buttonVariant: 'ghost',
        buttonSize: 'lg',
        onPress: () => {
          if (router.canGoBack()) {
            router.back()
          } else {
            router.replace('/(authenticated)')
          }
        },
      },
    })
  }, [router, showAlert, t])
}

function useSingleContactFeedbackSuccess() {
  const { showAlert } = useAlert()
  const router = useRouter()
  const { t } = useTranslations('feedback')

  return useCallback(
    async ({
      contactId,
      sendAt,
      sendMethod,
    }: {
      contactId: string
      sendAt?: SendScheduleKey
      sendMethod?: string
    }) => {
      const interestStatus = await trpcClient.feedback.contactInterestStatus
        .query({ contactId })
        .catch(() => null)

      const showInterestEdit = interestStatus?.canEdit ?? false

      showAlert({
        type: 'info',
        title: t(($) => $.requestFlow.requestSent),
        description: t(($) => $.requestFlow.requestSentDescription, {
          method:
            sendMethod === 'sms'
              ? t(($) => $.requestFlow.sendingMethodBySms)
              : t(($) => $.requestFlow.sendingMethodByEmail),
          timing: t(($) => $.requestFlow.sendScheduleOptions[sendAt || 'now']),
        }),
        children: showInterestEdit && !interestStatus?.realtorInterest && (
          <View className="mb-2 gap-2">
            <Heading3 className="text-center">{t(($) => $.requestFlow.addSearchTitle)}</Heading3>
            <Body className="text-pretty text-center text-sm/normal">
              <Trans
                t={t}
                i18nKey={($) => $.requestFlow.addSearchDescription}
                components={{ strong: <Text className="font-medium" /> }}
              />
            </Body>
          </View>
        ),
        cancelButton: false,
        actions: [
          showInterestEdit && {
            label: t(($) => $.requestFlow.updateLeadSearch),
            buttonVariant: 'primary',
            buttonSize: 'lg',
            shouldClose: true,
            onPress: () =>
              router.replace({
                pathname: `/(authenticated)/leads/[leadId]/update-search`,
                params: { leadId: contactId },
              }),
          },
          {
            label: t(($) => $.requestFlow.newRequest),
            buttonVariant: !showInterestEdit ? 'primary' : 'default',
            buttonSize: 'lg',
            shouldClose: true,
            onPress: () =>
              router.replace({
                pathname: `/(authenticated)/feedback-request/add`,
              }),
          },
          {
            label: t(($) => $.requestFlow.backToHome),
            buttonVariant: 'outline',
            buttonSize: 'lg',
            shouldClose: true,
            onPress: () =>
              router.replace({
                pathname: `/`,
              }),
          },
        ].filter(Boolean) as AlertAction[],
      })
    },
    [router, showAlert, t]
  )
}

export function CreateFeedbackRequestFlow({ initialContactId }: { initialContactId?: string }) {
  const { t } = useTranslations('feedback')
  const queryClient = useQueryClient()
  const userId = useUserId()
  const { showAlert } = useAlert()
  const router = useRouter()
  const { data: inboundAddress, isLoading: isInboundAddressLoading } = useUserInboundAddress()

  const alertUserProfileIncomplete = useIncompleteProfileDialog()
  const alertFeedbackError = useFeedbackErrorMessage()
  const alertSingleContactFeedbackSuccess = useSingleContactFeedbackSuccess()
  const [hasUnsavedContactRows, setHasUnsavedContactRows] = useState(false)
  const [hasInvalidContactSelection, setHasInvalidContactSelection] = useState(false)
  const [hasInvalidReviewContacts, setHasInvalidReviewContacts] = useState(false)

  const { data: profile } = useUserProfile()

  const isProfileComplete = useMemo(
    () => (profile ? isUserProfileComplete(profile) : true),
    [profile]
  )

  useEffect(() => {
    if (isProfileComplete === false) {
      alertUserProfileIncomplete()
    }
  }, [isProfileComplete, alertUserProfileIncomplete])

  const sendFeedbackMutation = useMutation(
    trpc.feedback.createFeedbackRequest.mutationOptions({
      onSuccess: async () => {
        void queryClient.invalidateQueries({
          queryKey: CONTACTS_QUERY_KEY(userId).all,
        })
      },
    })
  )

  const defaultSendingMethod = 'sms'
  const defaultSendTiming = 'now'

  const phases = useMemo(
    () =>
      createFlowPhases([
        {
          id: 'select-creation-method' as const,
          title: t(($) => $.requestFlow.stepSelectCreationMethod),
          schema: requestCreationMethodSchema,
          data: getInitialRequestCreationMethodPhaseData(),
        },
        {
          id: 'inbound-email' as const,
          title: t(($) => $.requestFlow.stepInboundEmail),
          condition: (data) => getRequestCreationMethod(data) === 'inbound-email',
          data: getInitialInboundEmailPhaseData(),
        },
        {
          id: 'contact-support' as const,
          title: t(($) => $.requestFlow.stepContactSupport),
          condition: (data) => getRequestCreationMethod(data) === 'contact-support',
        },
        {
          id: 'select-contact' as const,
          title: t(($) => $.requestFlow.stepSelectContact),
          schema: selectContactSchema,
          data: getInitialSelectContactPhaseData(initialContactId),
          condition: (data) => getRequestCreationMethod(data) === 'manual',
        },
        {
          id: 'request-review' as const,
          title: t(($) => $.requestFlow.stepSendRequest),
          condition: (data) => getRequestCreationMethod(data) === 'manual',
        },
        {
          id: 'request-config' as const,
          title: t(($) => $.requestFlow.stepSelectSendingMethod),
          usesCustomButtons: true,
          schema: requestConfigSchema,
          data: {
            sendTiming: defaultSendTiming,
            sendingMethod: defaultSendingMethod,
          },
          condition: (data) => getRequestCreationMethod(data) === 'manual',
        },
      ] as const),
    [initialContactId, defaultSendTiming, defaultSendingMethod, t]
  )

  const onComplete = useCallback(
    async (data: FlowData) => {
      const creationMethodData = requestCreationMethodSchema.safeParse(
        data['select-creation-method']
      )

      if (!creationMethodData.success) {
        return false
      }

      const { creationMethod } = creationMethodData.data

      if (creationMethod === 'inbound-email') {
        if (!inboundAddress) {
          return false
        }

        router.replace('/(authenticated)')
        return true
      }

      if (creationMethod === 'contact-support') {
        router.replace('/(authenticated)')
        return true
      }

      if (!isProfileComplete) {
        alertUserProfileIncomplete()
        return false
      }

      const contactData = selectContactSchema.safeParse(data['select-contact'])
      const configData = requestConfigSchema.safeParse(data['request-config'])

      if (!contactData.success || !configData.success) {
        return false
      }

      const { contactIds } = contactData.data
      const { sendTiming, sendingMethod } = configData.data

      if (contactIds.length === 0) {
        return false
      }

      const finalSendAt = sendingMethod === 'email' ? 'now' : sendTiming

      try {
        const result = await sendFeedbackMutation.mutateAsync({
          userId,
          contactIds,
          sendAt: finalSendAt,
          sendMethod: sendingMethod,
        })

        const failures: { error: SendFeedbackRequestError }[] =
          'failures' in result && Array.isArray(result.failures)
            ? (result.failures as { error: SendFeedbackRequestError }[])
            : []

        if (result.successCount === 0) {
          const firstFailure = failures[0]
          alertFeedbackError(firstFailure?.error ?? ('GENERAL_ERROR' as SendFeedbackRequestError))
          return false
        }

        if (contactIds.length === 1) {
          const [contactId] = contactIds

          await alertSingleContactFeedbackSuccess({
            contactId,
            sendAt: finalSendAt,
            sendMethod: sendingMethod,
          })
        } else {
          showAlert({
            type: result.failureCount > 0 ? 'info' : 'success',
            title:
              result.failureCount > 0
                ? t('requestFlow.multiContactPartialTitle')
                : t('requestFlow.multiContactSuccessTitle'),
            description:
              result.failureCount > 0
                ? t('requestFlow.multiContactPartialDescription', {
                    successCount: result.successCount,
                    failureCount: result.failureCount,
                  })
                : t('requestFlow.multiContactSuccessDescription', {
                    successCount: result.successCount,
                  }),
            actions: [
              {
                label: t('requestFlow.backToHome'),
                buttonVariant: 'primary',
                buttonSize: 'lg',
                shouldClose: true,
                onPress: () => {
                  router.replace('/(authenticated)')
                },
              },
            ],
            cancelButton: false,
          })
        }

        return true
      } catch (error) {
        console.error('Failed to send feedback request', error)
        alertFeedbackError('GENERAL_ERROR' as SendFeedbackRequestError)
        return false
      }
    },
    [
      alertUserProfileIncomplete,
      alertFeedbackError,
      isProfileComplete,
      sendFeedbackMutation,
      alertSingleContactFeedbackSuccess,
      showAlert,
      t,
      router,
      userId,
      inboundAddress,
    ]
  )

  const isNextDisabled = useCallback(
    (data: FlowData, currentPhaseId: (typeof phases)[number]['id']) => {
      if (currentPhaseId === 'select-creation-method') {
        const selectedCreationMethod = getRequestCreationMethod(data)

        if (!selectedCreationMethod) {
          return true
        }

        if (selectedCreationMethod === 'inbound-email' && isInboundAddressLoading) {
          return true
        }

        return false
      }

      if (currentPhaseId === 'inbound-email') {
        const inboundEmailPhaseData = parseInboundEmailPhaseData(data['inbound-email'])

        return (
          isInboundAddressLoading ||
          !inboundAddress ||
          (!inboundEmailPhaseData.hadInboundAddressBeforeEntering &&
            !inboundEmailPhaseData.hasSavedToContacts)
        )
      }

      if (currentPhaseId === 'select-contact') {
        const selectContactData = parseSelectContactPhaseData(
          data['select-contact'],
          initialContactId
        )

        if (
          selectContactData.contactIds.length === 0 ||
          !selectContactData.contactConsentConfirmed ||
          hasUnsavedContactRows ||
          hasInvalidContactSelection
        ) {
          return true
        }

        return false
      }

      if (currentPhaseId === 'request-review' && hasInvalidReviewContacts) {
        return true
      }

      return false
    },
    [
      hasInvalidContactSelection,
      hasInvalidReviewContacts,
      hasUnsavedContactRows,
      inboundAddress,
      initialContactId,
      isInboundAddressLoading,
    ]
  )

  const onNext = useCallback(
    async (
      data: FlowData,
      currentPhaseId: (typeof phases)[number]['id'],
      nextPhaseId: (typeof phases)[number]['id'] | null,
      updateFlowData: (
        phaseId: (typeof phases)[number]['id'],
        phaseData: FlowData[(typeof phases)[number]['id']],
        isUserUpdate?: boolean
      ) => void
    ) => {
      if (currentPhaseId === 'select-creation-method' && nextPhaseId === 'inbound-email') {
        const inboundEmailPhaseData = parseInboundEmailPhaseData(data['inbound-email'])

        updateFlowData('inbound-email', {
          ...inboundEmailPhaseData,
          hadInboundAddressBeforeEntering: !!inboundAddress,
        })
      }

      if (currentPhaseId === 'request-review') {
        if (!isProfileComplete) {
          alertUserProfileIncomplete()
          return false
        }
      }

      return true
    },
    [alertUserProfileIncomplete, inboundAddress, isProfileComplete]
  )

  return (
    <FlowProvider
      flowId="add-feedback-request-v3"
      phases={phases}
      flowTitle={t(($) => $.requestFlow.flowTitle)}
      onClose={(resetFlow) => {
        resetFlow()
        router.replace('/(authenticated)')
      }}
      onComplete={onComplete}
      onNext={onNext}
      isNextDisabled={isNextDisabled}
      isLoading={sendFeedbackMutation.isPending}
      failureResetPhase="request-review"
      completeLabel={(currentPhaseId) =>
        currentPhaseId === 'inbound-email' || currentPhaseId === 'contact-support'
          ? t(($) => $.requestFlow.completeAndGoHome)
          : t(($) => $.requestFlow.send)
      }>
      <FeedbackRequestPhaseRenderer
        initialContactId={initialContactId}
        onHasUnsavedContactRowsChange={setHasUnsavedContactRows}
        onHasInvalidContactSelectionChange={setHasInvalidContactSelection}
        onHasInvalidReviewContactsChange={setHasInvalidReviewContacts}
      />
    </FlowProvider>
  )
}

function FeedbackRequestPhaseRenderer({
  initialContactId,
  onHasUnsavedContactRowsChange,
  onHasInvalidContactSelectionChange,
  onHasInvalidReviewContactsChange,
}: {
  initialContactId?: string
  onHasUnsavedContactRowsChange: (hasUnsavedRows: boolean) => void
  onHasInvalidContactSelectionChange: (hasInvalidSelection: boolean) => void
  onHasInvalidReviewContactsChange: (hasInvalidContacts: boolean) => void
}) {
  const { data: flowData, currentPhaseId, updateData, nextPhase, canProceed } = useFlowManager()
  const [shouldAutoAdvanceCreationMethod, setShouldAutoAdvanceCreationMethod] = useState(false)

  const requestCreationMethodData = parseRequestCreationMethodPhaseData(
    flowData?.['select-creation-method']
  )
  const selectedCreationMethod = requestCreationMethodData.creationMethod

  const onSelectCreationMethod = useCallback(
    (creationMethod: FeedbackRequestCreationMethod) => {
      updateData('select-creation-method', {
        creationMethod,
      })
      setShouldAutoAdvanceCreationMethod(true)
    },
    [updateData]
  )

  useEffect(() => {
    if (
      !shouldAutoAdvanceCreationMethod ||
      currentPhaseId !== 'select-creation-method' ||
      !selectedCreationMethod ||
      !canProceed
    ) {
      return
    }

    const nextPhaseTimeout = setTimeout(() => {
      setShouldAutoAdvanceCreationMethod(false)
      nextPhase()
    }, 300)

    return () => {
      clearTimeout(nextPhaseTimeout)
    }
  }, [
    canProceed,
    currentPhaseId,
    nextPhase,
    selectedCreationMethod,
    shouldAutoAdvanceCreationMethod,
  ])

  const onSaveInboundEmailToContacts = useCallback(() => {
    updateData('inbound-email', {
      hasSavedToContacts: true,
    })
  }, [updateData])

  const updateSelectContactData = useCallback(
    (updater: (currentData: SelectContactPhaseData) => SelectContactPhaseData) => {
      updateData('select-contact', (currentData) =>
        updater(parseSelectContactPhaseData(currentData, initialContactId))
      )
    },
    [initialContactId, updateData]
  )

  const onUserSelectContactIds = useCallback(
    (newContactIds: string[]) => {
      updateSelectContactData((currentData) => ({
        ...currentData,
        contactIds: newContactIds,
      }))
    },
    [updateSelectContactData]
  )

  const onUserSetContactConsentConfirmed = useCallback(
    (contactConsentConfirmed: boolean) => {
      updateSelectContactData((currentData) => ({
        ...currentData,
        contactConsentConfirmed,
      }))
    },
    [updateSelectContactData]
  )

  const selectContactData = parseSelectContactPhaseData(
    flowData?.['select-contact'],
    initialContactId
  )
  const selectedContactIds = selectContactData.contactIds
  const contactConsentConfirmed = selectContactData.contactConsentConfirmed

  const requestConfigData = (flowData?.['request-config'] as
    | FeedbackRequestFlowData['request-config']
    | undefined) ?? {
    sendTiming: 'now' as const,
    sendingMethod: 'sms' as const,
  }

  if (currentPhaseId === 'select-creation-method') {
    return (
      <FeedbackRequestCreationMethodPhase
        selectedMethod={selectedCreationMethod}
        onSelect={onSelectCreationMethod}
      />
    )
  }

  if (currentPhaseId === 'inbound-email') {
    return <FeedbackRequestInboundEmailPhase onSaveToContactsPress={onSaveInboundEmailToContacts} />
  }

  if (currentPhaseId === 'contact-support') {
    return <FeedbackRequestSupportPhase />
  }

  if (currentPhaseId === 'select-contact') {
    return (
      <SelectContactPhase
        selectedContactIds={selectedContactIds}
        contactConsentConfirmed={contactConsentConfirmed}
        onUserSelectContactIds={onUserSelectContactIds}
        onChangeContactConsentConfirmed={onUserSetContactConsentConfirmed}
        onHasUnsavedRowsChange={onHasUnsavedContactRowsChange}
        onHasInvalidSelectionChange={onHasInvalidContactSelectionChange}
      />
    )
  }

  return (
    <>
      {(currentPhaseId === 'request-review' || currentPhaseId === 'request-config') && (
        <FeedbackReviewPhase
          selectedContactIds={selectedContactIds}
          onHasInvalidContactsChange={onHasInvalidReviewContactsChange}
        />
      )}

      {currentPhaseId === 'request-config' && (
        <FeedbackSendingSettingsModal
          selectedContactIds={selectedContactIds}
          requestData={requestConfigData}
          updateData={(key, data) => updateData(key, data)}
        />
      )}
    </>
  )
}

function SelectContactPhase({
  selectedContactIds,
  contactConsentConfirmed,
  onUserSelectContactIds,
  onChangeContactConsentConfirmed,
  onHasUnsavedRowsChange,
  onHasInvalidSelectionChange,
}: {
  selectedContactIds: string[]
  contactConsentConfirmed: boolean
  onUserSelectContactIds: (contactIds: string[]) => void
  onChangeContactConsentConfirmed: (contactConsentConfirmed: boolean) => void
  onHasUnsavedRowsChange: (hasUnsavedRows: boolean) => void
  onHasInvalidSelectionChange: (hasInvalidSelection: boolean) => void
}) {
  const { t } = useTranslations('feedback')
  const { t: tContact } = useTranslations('contacts')
  const { recalculateCanProceed } = useFlowManager()

  const { data: hasAnyContacts = false } = useHasAnyContactsForFeedback()
  const { data: selectedContactsByIds = [], isLoading: isLoadingSelectedContacts } =
    useContactsByIds(selectedContactIds)

  const getContactEligibility = useCallback(
    (contact: DisplayContact) => getContactFeedbackEligibility(contact),
    []
  )

  const isContactSelectable = useCallback(
    (contact: DisplayContact) => getContactEligibility(contact).canSend,
    [getContactEligibility]
  )

  const getContactIneligibilityMessage = useCallback(
    (contact: DisplayContact) => {
      const contactEligibility = getContactEligibility(contact)

      if (contactEligibility.canSend) {
        return null
      }

      return getFeedbackEligibilityErrorMessage(t, contactEligibility, false)
    },
    [getContactEligibility, t]
  )

  const onChangeSelectedContactIds = useCallback(
    (contactIds: string[]) => {
      const hasSelectionChanged =
        contactIds.length !== selectedContactIds.length ||
        contactIds.some(
          (validContactId, contactIdIndex) => validContactId !== selectedContactIds[contactIdIndex]
        )

      if (!hasSelectionChanged) {
        return
      }

      onUserSelectContactIds(contactIds)
      recalculateCanProceed()
    },
    [onUserSelectContactIds, recalculateCanProceed, selectedContactIds]
  )

  const selectedContactIssues = useMemo(() => {
    const selectedContactsById = new Map(
      selectedContactsByIds.map((selectedContact) => [selectedContact.id, selectedContact] as const)
    )
    const missingContactIds: string[] = []
    const ineligibleContacts: {
      contact: DisplayContact
      eligibility: ContactFeedbackEligibility
    }[] = []

    for (const selectedContactId of selectedContactIds) {
      const selectedContact = selectedContactsById.get(selectedContactId)

      if (!selectedContact) {
        missingContactIds.push(selectedContactId)
        continue
      }

      const eligibility = getContactEligibility(selectedContact)

      if (!eligibility.canSend) {
        ineligibleContacts.push({
          contact: selectedContact,
          eligibility,
        })
      }
    }

    return {
      missingContactIds,
      ineligibleContacts,
    }
  }, [selectedContactIds, selectedContactsByIds, getContactEligibility])

  const hasInvalidSelection = useMemo(() => {
    if (selectedContactIds.length === 0) {
      return false
    }

    if (isLoadingSelectedContacts) {
      return true
    }

    if (selectedContactIssues.missingContactIds.length > 0) {
      return true
    }

    if (selectedContactIssues.ineligibleContacts.length > 0) {
      return true
    }

    return false
  }, [selectedContactIds.length, isLoadingSelectedContacts, selectedContactIssues])

  useEffect(() => {
    onHasInvalidSelectionChange(hasInvalidSelection)
    recalculateCanProceed()
  }, [hasInvalidSelection, onHasInvalidSelectionChange, recalculateCanProceed])

  useEffect(() => {
    return () => {
      onHasUnsavedRowsChange(false)
      onHasInvalidSelectionChange(false)
    }
  }, [onHasUnsavedRowsChange, onHasInvalidSelectionChange])

  const onChangeHasUnsavedRows = useCallback(
    (hasUnsavedRows: boolean) => {
      onHasUnsavedRowsChange(hasUnsavedRows)
      recalculateCanProceed()
    },
    [onHasUnsavedRowsChange, recalculateCanProceed]
  )

  const onToggleContactConsentConfirmed = useCallback(
    (nextChecked: boolean) => {
      onChangeContactConsentConfirmed(nextChecked)
      recalculateCanProceed()
    },
    [onChangeContactConsentConfirmed, recalculateCanProceed]
  )

  const contactPhaseDescription = hasAnyContacts
    ? t(($) => $.requestFlow.selectMultipleContactsDescription1)
    : t(($) => $.requestFlow.selectContactDescription2)

  return (
    <View className="flex-1 gap-6">
      <SectionContent className="text-pretty text-lg/relaxed">
        {contactPhaseDescription}
      </SectionContent>

      {selectedContactIssues.missingContactIds.length > 0 && (
        <InfoAlert level="info" className="gap-2">
          <SectionContent className="text-pretty text-destructive">
            {t(($) => $.errors.contactNotFound)} {t(($) => $.errors.selectOtherContactOrAsset)}
          </SectionContent>
        </InfoAlert>
      )}

      <MultiRowContactSelect
        selectedContactIds={selectedContactIds}
        onChangeSelectedContactIds={onChangeSelectedContactIds}
        isContactSelectable={isContactSelectable}
        getContactIneligibilityMessage={getContactIneligibilityMessage}
        onHasUnsavedRowsChange={onChangeHasUnsavedRows}
        limitReachedMessage={
          <Text className="text-pretty text-center font-light text-lg/normal text-muted-foreground">
            {t('requestFlow.maxContactsReached', {
              maxContacts: 10,
            })}
          </Text>
        }
      />

      <ContactConsentConfirmation
        testID="feedback-contact-consent-confirmation"
        checked={contactConsentConfirmed}
        onCheckedChange={onToggleContactConsentConfirmed}
        label={tContact(($) => $.form.consentLabel, { count: selectedContactIds.length })}
      />

      <FeedbackRequestAcknowledgement className="mt-auto pt-8" />
    </View>
  )
}

function FlowStep({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <View className={cn('mt-4 self-center py-1 first:mt-0', className)}>
      <Text className="text-center font-medium text-xs uppercase text-secondary">{children}</Text>
    </View>
  )
}

function SmsMessagePreview({
  contactName,
  realtorName,
  companyName,
  className,
}: {
  contactName: string
  realtorName: string
  companyName?: string
  className?: string
}) {
  const { t } = useTranslations('feedback')

  const smsText = t('notifications.sms.feedbackRequest', {
    contactName,
    // eslint-disable-next-line i18next/no-literal-string
    feedbackUrl: 'https://aikoa.ai/ABC123',
    realtorName,
    // eslint-disable-next-line i18next/no-literal-string
    companyNamePart: companyName ? ` / ${companyName}` : '',
  })

  return (
    <View className={cn('gap-2', className)}>
      <View className="flex-row items-center gap-2">
        <Icon name="message-square" library="feather" size={12} className="text-muted-foreground" />
        <Label className="font-medium text-xs text-muted-foreground">
          {t('requestFlow.smsPreviewLabel')}
        </Label>
      </View>
      <View className="rounded-2xl border border-tertiary-foreground bg-tertiary-foreground/80 p-4">
        <Text className="text-sm/relaxed text-primary">{smsText}</Text>
      </View>
    </View>
  )
}

function FeedbackReviewPhase({
  selectedContactIds,
  onHasInvalidContactsChange,
}: {
  selectedContactIds: string[]
  onHasInvalidContactsChange: (hasInvalidContacts: boolean) => void
}) {
  const { t } = useTranslations('feedback')
  const { goToPhase } = useFlowManager()

  const { data: selectedContacts = [], isLoading: isLoadingContacts } =
    useContactsByIds(selectedContactIds)
  const { data: profile } = useUserProfile()

  const isSingleContact = selectedContactIds.length === 1
  const selectedContactId = isSingleContact ? selectedContactIds[0] : ''

  const [isInterestFlowOpen, setInterestFlowOpen] = useState(false)

  const contactEligibilityById = useMemo(() => {
    const eligibilityById = new Map<string, ContactFeedbackEligibility>()

    for (const contact of selectedContacts) {
      eligibilityById.set(contact.id, getContactFeedbackEligibility(contact))
    }

    return eligibilityById
  }, [selectedContacts])

  const allContactsInvalid = useMemo(() => {
    if (selectedContacts.length === 0) {
      return false
    }

    return selectedContacts.every((contact) => !contactEligibilityById.get(contact.id)?.canSend)
  }, [selectedContacts, contactEligibilityById])

  useEffect(() => {
    onHasInvalidContactsChange(allContactsInvalid)
  }, [allContactsInvalid, onHasInvalidContactsChange])

  const firstContactName = truncateName(selectedContacts[0]?.name || '', 10, 1)
  // eslint-disable-next-line i18next/no-literal-string
  const realtorName = truncateName(profile?.name || 'Välitttäjä', 16, 2)
  const companyName = truncateName(profile?.organisation?.name || '', 12, 1)

  return (
    <>
      <View className="gap-4">
        {isLoadingContacts && <LoadingView />}
        {selectedContacts.length > 0 && (
          <View className="relative gap-6">
            <FlowStep>
              {selectedContacts.length === 1
                ? t(($) => $.requestFlow.sendFeedbackLink_one)
                : t(($) => $.requestFlow.sendFeedbackLink_other)}
            </FlowStep>
            {firstContactName && (
              <SmsMessagePreview
                contactName={firstContactName}
                realtorName={realtorName}
                companyName={companyName}
                className="mx-2"
              />
            )}
            <View className="z-1 mx-2 gap-2">
              {selectedContacts.map((contact) => {
                const contactEligibility = contactEligibilityById.get(contact.id)

                return (
                  <ContactRow contact={contact} key={contact.id}>
                    {contactEligibility && !contactEligibility.canSend && (
                      <FeedbackRequestEligibilityMessage
                        eligibility={contactEligibility}
                        className="mx-0"
                      />
                    )}
                  </ContactRow>
                )
              })}
            </View>
            {isSingleContact && !!selectedContactId && (
              <InterestFlowBottomSheet
                isOpen={isInterestFlowOpen}
                onOpenChange={setInterestFlowOpen}
                contactId={selectedContactId}
                allowNew
                showInterestDetails
              />
            )}
          </View>
        )}

        {allContactsInvalid && selectedContacts.length > 0 && (
          <Button variant="outline" size="lg" onPress={() => goToPhase('select-contact')}>
            {t(($) => $.requestFlow.selectOtherContact)}
          </Button>
        )}
      </View>
    </>
  )
}

function FeedbackRequestEligibilityMessage({
  eligibility,
  className,
}: {
  eligibility: ContactFeedbackEligibility
  className?: string
}) {
  const { t } = useTranslations('feedback')

  return (
    <View
      className={cn(
        'mx-2 rounded-xl border border-destructive/30 bg-destructive/5 p-4',
        className
      )}>
      <SectionContent className="text-pretty text-destructive">
        {getFeedbackEligibilityErrorMessage(t, eligibility, true)}
      </SectionContent>
    </View>
  )
}
